/**
 * 本页（家长端 · 学校作业）用的**内联矢量图标**，vendored 到这里，**不引任何依赖**。
 *
 * ## 为什么是"抄进来"而不是"装一个图标库"（方案 1）
 *
 * 2026-10-14 用户实测："那些按钮 icon 是你自己做的吗？是矢量图标吗？怎么和原来的一样？
 * 没变化呀！如果你做不出来，可以去一些开源免费的图标网站找，同一样式的。"
 *
 * 他说得对：改之前那些"图标"其实是 **emoji**（🕐 ⚙️ 📋 🗑 ↑ ↓ ← →）——
 * 彩色字形、宽度随字形走、**不认 `currentColor`**（所以"删除"按钮的图标没法跟着
 * 危险色走），上一轮只统一了外框尺寸、没换图标本身，所以看不出变化。
 *
 * 现在改成 **Lucide**（https://lucide.dev）的线条图标：24×24 网格、`stroke-width=2`、
 * 圆头圆角（`stroke-linecap/linejoin=round`），**全页同一套**，
 * 视觉重量/圆角/线粗完全一致。
 *
 * 为什么是 vendored 而不是 `npm i lucide-react`：
 * - **零依赖、零 lockfile 改动** → 服务器构建镜像那条链路一个字节都不用重新验；
 * - 这一页只需要 8 个图标，装一个库是"为 8 个图标引入一次部署风险"；
 * - 代价是**升级要手动**（想换版本就重新对着 lucide 的 SVG 抄一遍这几条 `d`）。
 *   ⚠️ 抄的时候**只抄几何数据**（`path`/`circle`/`line` 的属性），
 *   视图盒、`stroke-width`、圆角全部由下面的 `Icon` 统一给，
 *   这样 8 个图标的线粗与圆角**不可能各写各的**。
 *
 * ## 颜色：全部继承 `currentColor`
 *
 * `stroke="currentColor"`（Lucide 本身就是描边图标），所以：
 * - `variant="danger"` 的删除按钮（红底白字）→ 图标自动变白；
 * - `disabled:text-slate-400` 的禁用按钮 → 图标自动变灰；
 * - 以后想把某个按钮的图标改成红色，不用碰这个文件。
 * **emoji 做不到这件事**，这正是换掉它的硬需求之一。
 *
 * 调用点（`ParentHomeworkPage` 的 `IconAction` / `FoldToggle`）负责：
 * - `aria-hidden="true"`（图标是装饰性的，语义由按钮的 `aria-label` / `title` 给，见下）；
 * - 尺寸统一 **20px**（`size={20}` = 这个文件的默认值），包在 44×44 的 `size="icon"` 按钮里。
 *
 * ⚠️ **本文件只服务于这一页**：不要把别的页面里的 emoji 顺手换掉
 * （本项目的一条教训：不要因为"设计一致性"去改用户没抱怨的东西）。
 * 真要全站换，那是一次单独的、需要先跟用户确认的改动。
 *
 * ---------------------------------------------------------------------------
 * 许可（vendored 代码必须带着它一起走）：
 *
 * ISC License
 *
 * Copyright (c) for portions of Lucide are held by Cole Bemis 2013-2022 as part of
 * Feather (MIT). All other copyright (c) for Lucide are held by Lucide Contributors 2022.
 *
 * Permission to use, copy, modify, and/or distribute this software for any
 * purpose with or without fee is hereby granted, provided that the above
 * copyright notice and this permission notice appear in all copies.
 *
 * THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
 * WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
 * MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
 * ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
 * WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
 * ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
 * OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
 *
 * 抄录来源：`lucide-static@0.454.0`（https://unpkg.com/lucide-static@0.454.0/icons/）。
 * ---------------------------------------------------------------------------
 */
import type { ReactNode, SVGProps } from 'react';

/** 所有图标的公共参数。`size` 默认 20 —— 与 44×44 按钮的留白比例正合适。 */
export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  /** 边长（px），同时写进 `width` / `height`。默认 20。 */
  size?: number;
}

/**
 * 本文件导出的每一个图标都是这个类型。
 *
 * 存在的理由：**`navConfig.ts` 是 `.ts` 而不是 `.tsx`**（它只有数据、没有视图），
 * 所以那里没法写 `<IconWrench />` 这样的 JSX。于是导航项存的是**组件本身**，
 * 由 `AppShell` 在渲染时实例化 —— 数据与视图仍然分开，而且图标尺寸只由
 * `AppShell` 一处决定（不会出现"某一项自己写了字号"）。
 */
export type IconComponent = (props: IconProps) => ReactNode;

/**
 * 图标底座：**视图盒、线粗、圆角、颜色**只在这里出现一次。
 *
 * ⚠️ `aria-hidden` 写死在这里：这些图标全是**装饰性的**，
 * 每个图标按钮的语义由按钮自己的 `aria-label`（+ `title`）承担
 * —— 读屏时念的是"删掉这项作业"，而不是"垃圾桶图形"。
 */
function Icon({ size = 20, children, ...rest }: IconProps & { children: ReactNode }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

/* ---------- 日期选择：前一天 / 后一天（原来是 ← → 两个字符） ---------- */

export function IconChevronLeft(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m15 18-6-6 6-6" />
    </Icon>
  );
}

export function IconChevronRight(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m9 18 6-6-6-6" />
    </Icon>
  );
}

/* ---------- 科目顺序：上移 / 下移（原来是 ↑ ↓ 两个字符） ---------- */

export function IconArrowUp(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m5 12 7-7 7 7" />
      <path d="M12 19V5" />
    </Icon>
  );
}

export function IconArrowDown(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 5v14" />
      <path d="m19 12-7 7-7-7" />
    </Icon>
  );
}

/* ---------- 复制这个科目 / 复制上次的结构（原来是 📋） ---------- */

export function IconCopy(props: IconProps) {
  return (
    <Icon {...props}>
      <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
    </Icon>
  );
}

/* ---------- 删除（原来是 🗑），危险色靠 `currentColor` 继承 ---------- */

export function IconTrash(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3 6h18" />
      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
      <line x1="10" x2="10" y1="11" y2="17" />
      <line x1="14" x2="14" y1="11" y2="17" />
    </Icon>
  );
}

/* ---------- 折叠区「时间」（原来是 🕐） ---------- */

export function IconClock(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </Icon>
  );
}

/* ---------- 折叠区「更多：分值 / 目标值」（原来是 ⚙️） ---------- */

export function IconSettings(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </Icon>
  );
}

/* ===========================================================================
 * 以下是 2026-10-14 为**家长端语文听写页**补的图标。
 *
 * 与上面那 8 个**同一套做法**：只抄 Lucide 的几何数据，视图盒 / 线粗 / 圆角 /
 * 颜色一律由上面那个 `Icon` 底座给，所以新旧图标的视觉重量必然一致。
 * 调用点同样负责 `aria-hidden`（底座已写死）与 44×44 的 `size="icon"` 按钮。
 *
 * 为什么补它们而不是用 emoji：用户 2026-10-14 的原话是"是矢量图标吗？怎么和
 * 原来的一样？没变化呀"—— emoji 是彩色字形、宽度随字形走、**不认 `currentColor`**，
 * 所以"编辑/删除/恢复"这类按钮的颜色语义（危险色、禁用灰）根本传达不出来。
 *
 * 抄录来源同上：`lucide-static@0.454.0`。
 * =========================================================================== */

/* ---------- 批量导入（整块粘贴） ---------- */

export function IconUpload(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" x2="12" y1="3" y2="15" />
    </Icon>
  );
}

/* ---------- 编辑（原来的入口是"点一下拼音"） ---------- */

export function IconPencil(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
      <path d="m15 5 4 4" />
    </Icon>
  );
}

/* ---------- 新增 ---------- */

export function IconPlus(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M5 12h14" />
      <path d="M12 5v14" />
    </Icon>
  );
}

/* ---------- 确认（提交导入） ---------- */

export function IconCheck(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M20 6 9 17l-5-5" />
    </Icon>
  );
}

/* ---------- 恢复自动值（拼音回到内置字表） ---------- */

export function IconRotateCcw(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
    </Icon>
  );
}

/* ---------- 课本 ---------- */

export function IconBook(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20" />
    </Icon>
  );
}

/* ---------- 展开/收起（说明与折叠区） ---------- */

export function IconChevronDown(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m6 9 6 6 6-6" />
    </Icon>
  );
}

/* ---------- 警告（导入错误行 / 后端缺陷提示） ---------- */

export function IconAlertTriangle(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </Icon>
  );
}

/* ===========================================================================
 * 以下是 2026-10-14 导航重构（「共享」→「工具」）补的图标。
 *
 * 与上面所有图标**同一套做法**（只有几何数据来自 Lucide，视图盒 / 线粗 / 圆角 /
 * 颜色一律由上面的 `Icon` 底座给），所以新旧图标的视觉重量必然一致。
 *
 * ⚠️ 为什么主导航要用矢量图标而不是 emoji：主导航是**每一页都在**的元素，
 *    而 emoji 是彩色字形 —— 不认 `currentColor`，所以选中态的 `brand-700`
 *    根本染不到它，四个 tab 里会有一个看起来"没被选中"。用户 2026-10-14 的
 *    原话是"是矢量图标吗？怎么和原来的一样？没变化呀"。
 *    （本轮**只**换「工具」这一项，其余三项的 emoji 不动 —— 用户只点名了它。）
 *
 * 抄录来源同上：`lucide-static@0.454.0`。
 * =========================================================================== */

/* ---------- 工具（主导航「工具」tab） ---------- */

export function IconWrench(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </Icon>
  );
}

/* ---------- 工具页里的一项工具（语文听写） ---------- */

export function IconBookOpen(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 7v14" />
      <path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" />
    </Icon>
  );
}

/* ---------- 共享（工具页里的共享收件箱 / 共享入口） ---------- */

export function IconShare2(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.59" x2="15.42" y1="13.51" y2="17.49" />
      <line x1="15.41" x2="8.59" y1="6.51" y2="10.49" />
    </Icon>
  );
}

/* ---------- 收件箱（待处理的共享邀请） ---------- */

export function IconInbox(props: IconProps) {
  return (
    <Icon {...props}>
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </Icon>
  );
}

/* ===========================================================================
 * 以下是 2026-10-15 为**语文听写第三步（听写会话播放器 + 家长录音）**补的图标。
 *
 * 与上面所有图标**同一套做法**（只有几何数据来自 Lucide，视图盒 / 线粗 / 圆角 /
 * 颜色一律由上面的 `Icon` 底座给），所以新旧图标的视觉重量必然一致。
 *
 * 为什么要补而不是用 emoji：播放器的按钮（播放/暂停/重播/跳过/录音）**每一秒都在
 * 被看**，而且它们的状态要靠颜色表达（"正在放"用主色、"跳过"是次要动作）。
 * emoji 是彩色字形、**不认 `currentColor`** —— 那几个按钮的选中态根本染不上色，
 * 这正是 2026-10-14 用户报"怎么和原来的一样？没变化呀"的同一个原因。
 *
 * 抄录来源同上：`lucide-static@0.454.0`。
 * =========================================================================== */

/* ---------- 播放 / 暂停（听写播放器的主按钮） ---------- */

export function IconPlay(props: IconProps) {
  return (
    <Icon {...props}>
      <polygon points="6 3 20 12 6 21 6 3" />
    </Icon>
  );
}

export function IconPause(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="14" y="4" width="4" height="16" rx="1" />
      <rect x="6" y="4" width="4" height="16" rx="1" />
    </Icon>
  );
}

/* ---------- 重播这一题 ---------- */

export function IconReplay(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <path d="M3 3v5h5" />
    </Icon>
  );
}

/* ---------- 跳过这一题 ---------- */

export function IconSkipForward(props: IconProps) {
  return (
    <Icon {...props}>
      <polygon points="5 4 15 12 5 20 5 4" />
      <line x1="19" x2="19" y1="5" y2="19" />
    </Icon>
  );
}

/* ---------- 家长录音（覆盖 TTS 的唯一可靠办法） ---------- */

export function IconMic(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 19v3" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <rect x="9" y="2" width="6" height="13" rx="3" />
    </Icon>
  );
}

/* ---------- 听写历史（列表入口） ---------- */

export function IconHistory(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
      <path d="M12 7v5l4 2" />
    </Icon>
  );
}

/* ---------- 音量（朗读来源是浏览器语音时的提示） ---------- */

export function IconVolume2(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z" />
      <path d="M16 9a5 5 0 0 1 0 6" />
      <path d="M19.364 18.364a9 9 0 0 0 0-12.728" />
    </Icon>
  );
}

/* ===========================================================================
 * 以下是 2026-10-16 为**听写预习卡（田字格 + 拼音 + 笔顺动画）**补的图标。
 *
 * 与上面所有图标**同一套做法**（只有几何数据来自 Lucide，视图盒 / 线粗 / 圆角 /
 * 颜色一律由上面的 `Icon` 底座给），所以新旧图标的视觉重量必然一致。
 *
 * 抄录来源同上：`lucide-static@0.454.0`。
 * =========================================================================== */

/* ---------- 预习（听写会话页上的「先预习一下」入口） ---------- */

export function IconEye(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
      <circle cx="12" cy="12" r="3" />
    </Icon>
  );
}

